import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import * as Sentry from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';

import { AppModule } from './app/app.module';
import { mobileBeforeSend } from './app/sentry/before-send';
import { setNativeVersionTags } from './app/sentry/set-native-version-tags';
import { environment } from './environments/environment';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import './extensions';
import { platformBrowser } from '@angular/platform-browser';

if (environment.production) {
  enableProdMode();
}

if (environment.sentry.enabled) {
  Sentry.init(
    {
      dsn: environment.sentry.dsn,
      environment: environment.sentry.environment,
      release: environment.releaseVersion,
      integrations: [SentryAngular.browserTracingIntegration()],
      tracePropagationTargets: environment.sentry.tracingOrigins,

      // Offline, expired session and request timeouts are sampled and sent as warnings.
      beforeSend: mobileBeforeSend,

      // Child spans dropped before sending. A pageload carries ~210 of them and almost all are
      // browser auto-instrumentation: one span per file fetched, per performance mark, per
      // navigation-timing step. In the WebView most of those files come off the device's own
      // disk, so they time nothing. The pageload and ui.interaction transactions stay, as do the
      // http.client spans to our own API, which are what links a trace to the backend.
      //
      // Web vitals are unaffected: lcp/cls/ttfb/fcp are measurements on the transaction, not spans.
      // Never match a root span op (pageload, navigation, ui.interaction.*) - that drops the whole
      // transaction.
      ignoreSpans: [
        { op: /^resource\./ },
        { op: /^browser\./ },
        { op: 'mark' },
        { op: 'measure' },
        { op: 'paint' },
        { op: 'ui.long-animation-frame' },
        { op: 'ui.long-task' },
        { op: 'file.read' },
        { name: /^Zone(:.*)?$/ },
        { name: /mark_feature_usage/ },
        // Our own bundled files, read off the device by relative URL, so they time nothing.
        // Anchored, so calls to our API cannot match - those are absolute.
        { name: /^GET \/assets\// },
        // Third parties we do not debug from traces. These match span descriptions, so they only
        // apply to http.client spans - every resource.* span is already dropped by op above. A
        // hostname that only ever serves scripts or assets does nothing here.
        { name: /app\.productfruits\.com/ },
        { name: /events\.launchdarkly\.com/ },
        { name: /app\.launchdarkly\.com/ },
        { name: /talkjs\.com/ },
        { name: /maps\.googleapis\.com/ },
      ],

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.01,
    },
    SentryAngular.init,
  );
  void setNativeVersionTags();
}

defineCustomElements(window);

platformBrowser()
  .bootstrapModule(AppModule, {
    applicationProviders: [provideZoneChangeDetection()],
  })
  .catch((err) => console.error(err));
